// src/components/desk/NegotiationModal.jsx — the negotiation timeline
// ----------------------------------------------------------------------------
// Spec: core/MEETINGS_MODEL.md §6 (the model) and §11 (this design). Read §11
// before changing anything here — several things that look like omissions are
// rulings.
//
// > "It's not about editing numbers. It's about giving every agent the complete
// >  commercial story between one property and one client. Real estate is
// >  surprisingly good at forgetting why someone offered 10% below asking and
// >  then reinventing the argument three weeks later."   — owner, 2026-07-24
//
// ⚠️ NOTHING HERE IS EDITABLE. Every control appends an EVENT; no control
// overwrites a price. That is the entire point — an audit trail instead of a
// mysterious final number. If someone asks for "just let me fix the amount", the
// answer is another event, not a PATCH. There is no update endpoint to call.
//
// ⚠️ NO "PROBABILITY OF AGREEMENT". It was asked for and deliberately not built
// (§11.3): we have no model of what closes, and a percentage inferred from a
// small gap is a guess wearing a confidence badge — agents either trust it
// wrongly or learn to ignore the whole panel. `gap_history` (7000 → 4000 → 2000)
// shows the same thing honestly and lets the agent draw the conclusion.
//
// ⚠️ MONEY KINDS ARE AGENT-ONLY (deposit / contract / closing / fell_through).
// Enforced by the handler AND a CHECK constraint; this UI is not the gate.

import { useCallback, useEffect, useState } from 'react'
import { useDeliberateBackdropDismiss } from '../../hooks/useDeliberateBackdropDismiss.js'
import { t, tLog } from '../../lib/i18n.js'
import { Icon } from '../Icon.jsx'
import { input, btn, pill, timeAgo } from './primitives.jsx'
import {
  appendNegotiationEvent, getNegotiation, getNegotiations, openNegotiation,
} from '../../db/core.js'
import { parseMoneyInput, sanitizeMoneyInput } from '../../lib/moneyInput.js'

const CCY = ['USD', 'GEL', 'EUR']

// ⚠️ A negotiated price is a LITERAL QUOTED NUMBER and is never converted for
// display. The whole thread is "who said what number" — showing "≈$196,400" next
// to an event nobody ever said is how a snapshot starts lying (the same trap as
// the posted-snapshot currency toggle). Amount + its own currency, always.
const money = (amount, currency) =>
  amount == null || amount === '' ? null
    : `${Number(amount).toLocaleString('en-US')} ${currency || ''}`.trim()

// Stage → how it should LOOK. Stage itself is folded from the events server-side
// (§6.3) and is never a stored column, so this map is presentation only.
const STAGE_STYLE = {
  open: 'border-border text-muted-foreground',
  negotiating: 'border-blue-500/50 text-blue-600 dark:text-blue-400',
  accepted: 'border-emerald-500/50 text-emerald-600 dark:text-emerald-400',
  deposit: 'border-emerald-500/50 text-emerald-600 dark:text-emerald-400',
  contract: 'border-emerald-500/50 text-emerald-600 dark:text-emerald-400',
  closed: 'border-emerald-600/60 text-emerald-700 dark:text-emerald-300 font-semibold',
  rejected: 'border-border text-muted-foreground',
  withdrawn: 'border-border text-muted-foreground',
  fell_through: 'border-destructive/50 text-destructive',
}

// The compose forms. `sides` = which actor_side choices this kind offers; a kind
// with one fixed side never asks. `priced` = needs an amount (the server rejects
// a priced kind without one, and so does a CHECK).
const KIND_FORM = {
  proposal:        { sides: ['client', 'owner'], priced: true },
  counterproposal: { sides: ['client', 'owner'], priced: true },
  acceptance:      { sides: ['client', 'owner'], priced: false },
  rejection:       { sides: ['client', 'owner'], priced: false },
  withdrawal:      { sides: ['client', 'owner'], priced: false },
  note:            { sides: [], priced: false },
  deposit:         { sides: [], priced: true },
  contract:        { sides: [], priced: false },
  closing:         { sides: [], priced: true },
  fell_through:    { sides: [], priced: false },
}
const TERMINAL_KINDS = new Set(['withdrawal', 'closing', 'fell_through'])

// ── One event in the story ───────────────────────────────────────────────────
// §11.2: every proposal answers five questions — WHO made it, WHEN, HOW (agent /
// meeting / client portal), WHY (the note), and WHAT CHANGED. A row that answers
// four of them is the row an agent has to phone three people about.
function EventRow({ e, prev }) {
  const side = e.actor_side ? t(`nego_side_${e.actor_side}`) : null
  const priced = e.amount != null
  // WHAT CHANGED — how far THIS side moved from its own last number. ⚠️ It must
  // compare against the same side's previous position, not simply the previous
  // priced event: against the other side's number the "delta" is just the gap
  // restated, so an owner countering 202,000 after a 195,000 offer read "+7,000"
  // as though the owner had raised their price by 7,000 when they had not yet
  // named one. Caught in the render check, 2026-07-24.
  const delta = priced && prev?.amount != null && e.currency === prev.currency
    ? Number(e.amount) - Number(prev.amount) : null
  // Moving TOWARD the other side is the encouraging direction, and which way that
  // is depends on who you are: a client coming up and an owner coming down are
  // the same news.
  const closing = delta != null && (e.actor_side === 'client' ? delta > 0 : delta < 0)
  return (
    <div className="relative pl-5 pb-3 last:pb-0">
      <span className="absolute left-0 top-1.5 w-2 h-2 rounded-full bg-border" />
      <div className="flex items-baseline gap-2 flex-wrap">
        <span className="text-xs font-medium">
          {side ? `${side} · ` : ''}{t(`nego_kind_${e.kind}`)}
        </span>
        <span className="text-xs text-muted-foreground" title={new Date(e.at).toLocaleString()}>
          {timeAgo(e.at)}
        </span>
        {/* HOW: a number the client typed into a public page is not the same fact
            as one an agent wrote down, and the thread must say which. */}
        {e.source === 'client_portal' && (
          <span className={pill('neutral', { extra: 'text-muted-foreground' })}>
            {t('nego_via_portal')}
          </span>
        )}
      </div>
      {priced && (
        <div className="text-sm font-semibold tabular-nums">
          {money(e.amount, e.currency)}
          {delta != null && delta !== 0 && (
            <span className={`ml-2 text-xs font-normal ${closing ? 'text-emerald-600 dark:text-emerald-400' : 'text-amber-600 dark:text-amber-400'}`}>
              {delta > 0 ? '+' : ''}{Number(delta).toLocaleString('en-US')}
            </span>
          )}
        </div>
      )}
      {e.message && <p className="text-xs text-muted-foreground italic mt-0.5">“{e.message}”</p>}
      {/* 0201 — WHICH viewing produced this number. This single line is why the
          per-event meeting column exists: six months later an agent opening the
          property understands the negotiation instead of phoning three people. */}
      {e.meeting_id && (
        <p className="text-xs text-muted-foreground mt-0.5">
          <Icon name="calendar" size={11} /> {tLog('nego_from_meeting', { id: String(e.meeting_id).slice(0, 8) })}
        </p>
      )}
    </div>
  )
}

// ── The compose form ─────────────────────────────────────────────────────────
function Compose({ kind, thread, meetingId, onCancel, onDone }) {
  const cfg = KIND_FORM[kind]
  const [side, setSide] = useState(cfg.sides[0] || null)
  const [amount, setAmount] = useState('')
  const [currency, setCurrency] = useState(thread.currency || thread.negotiation?.seed_currency || 'USD')
  const [message, setMessage] = useState('')
  const [busy, setBusy] = useState(false)
  const [err, setErr] = useState('')

  // ⚠️ §6.7 SURFACED WHERE THE MISTAKE WOULD BE MADE. You accept the OTHER
  // side's number, and the accepted amount IS the deal price — so the agent is
  // told, before they commit, exactly which number this locks in. The server
  // folds it the same way; this only mirrors that rule so it is not a surprise.
  const acceptsAmount = kind !== 'acceptance' ? null
    : side === 'owner' ? thread.client_position
    : side === 'client' ? thread.owner_position
    : null
  // review 2026-09-23: the locked number carries the ACCEPTED side's own currency (the
  // latest-position panel already does this) — a thread-level label misreports mixed threads.
  const acceptsCurrency = side === 'owner' ? (thread.client_currency || thread.currency)
    : side === 'client' ? (thread.owner_currency || thread.currency)
    : thread.currency
  // review 2026-09-23: ONE reading of a typed amount (src/lib/moneyInput.js) — "120,000"
  // is thousands and "550,50" is tetri here, as in every other money box.
  const parsedAmount = parseMoneyInput(amount)

  async function submit() {
    if (busy) return
    if (cfg.priced && parsedAmount == null) { setErr(t('nego_err_amount')); return }
    setBusy(true); setErr('')
    try {
      await appendNegotiationEvent(thread.negotiation.id, {
        kind, actorSide: side, meetingId,
        amount: cfg.priced ? parsedAmount : null,
        currency: cfg.priced ? currency : null,
        message,
      })
      onDone()
    } catch (e) { setErr(e.message); setBusy(false) }
  }

  return (
    <div className="rounded-lg border border-primary/40 bg-primary/5 p-3 space-y-2">
      <p className="text-xs font-medium">{t(`nego_kind_${kind}`)}</p>

      {cfg.sides.length > 0 && (
        <div className="flex gap-1.5">
          {cfg.sides.map(s => (
            <button key={s} type="button" onClick={() => setSide(s)}
              className={pill('none', { door: true, extra:
                s === side ? 'border-primary bg-primary/10 text-primary' : 'border-border text-muted-foreground hover:border-primary/40' })}>
              {t(kind === 'acceptance' ? `nego_accepts_${s}` : `nego_side_${s}`)}
            </button>
          ))}
        </div>
      )}

      {acceptsAmount != null && (
        <p className="text-xs text-emerald-700 dark:text-emerald-300">
          <Icon name="check" size={11} /> {tLog('nego_accept_locks', { amount: money(acceptsAmount, acceptsCurrency) })}
        </p>
      )}
      {kind === 'acceptance' && acceptsAmount == null && (
        <p className="text-xs text-amber-600 dark:text-amber-400">
          <Icon name="alert-triangle" size={11} /> {t('nego_accept_no_number')}
        </p>
      )}

      {cfg.priced && (
        <div className="flex gap-2">
          <input className={input} inputMode="decimal" value={amount} placeholder={t('nego_amount_ph')}
            onChange={e => setAmount(sanitizeMoneyInput(e.target.value))} />
          <select className={input + ' w-24'} value={currency} onChange={e => setCurrency(e.target.value)}>
            {CCY.map(c => <option key={c} value={c}>{c}</option>)}
          </select>
        </div>
      )}

      {/* WHY. Optional, but this is the field that stops the argument being
          reinvented three weeks later — so it is asked for every time, not
          hidden behind an "add a note" toggle. */}
      <textarea className={input} rows={2} value={message} placeholder={t('nego_why_ph')}
        onChange={e => setMessage(e.target.value)} />

      {/* Stated plainly: this closes the thread. Re-engagement opens a NEW
          negotiation (§6.2), which is what makes "accepted then fell through"
          countable — so it is not something to discover afterwards. */}
      {TERMINAL_KINDS.has(kind) && (
        <p className="text-xs text-muted-foreground">{t('nego_terminal_hint')}</p>
      )}
      {err && <p className="text-xs text-destructive">{err}</p>}
      <div className="flex gap-2">
        <button className={btn('primary') + ' flex-1'} disabled={busy} onClick={submit}>
          {busy ? t('nego_saving') : t('nego_record_btn')}
        </button>
        <button className={btn('ghost')} onClick={onCancel}>{t('meeting_cancel_btn')}</button>
      </div>
    </div>
  )
}

// ── The modal ────────────────────────────────────────────────────────────────
// Opens by (property, client) — the pair IS the identity, so the same thread is
// reached from the property side, the client side and a meeting without any of
// them needing to know about the others.
export function NegotiationModal({
  negotiationId, propertyId, clientPartyId, requirementId, intent,
  propertyLabel, clientLabel, meetingId, onClose,
}) {
  const backdropDismiss = useDeliberateBackdropDismiss(onClose)
  const [thread, setThread] = useState(null)
  const [err, setErr] = useState('')
  const [composing, setComposing] = useState(null)

  const load = useCallback(async id => {
    try { setThread(await getNegotiation(id)) } catch (e) { setErr(e.message) }
  }, [])

  useEffect(() => {
    let gone = false
    ;(async () => {
      try {
        // Idempotent: an existing live thread for this pair is returned rather
        // than a second conversation about the same thing.
        const id = negotiationId
          || (await openNegotiation({ propertyId, clientPartyId, requirementId, intent })).negotiation.id
        if (!gone) await load(id)
      } catch (e) { if (!gone) setErr(e.message) }
    })()
    return () => { gone = true }
  }, [negotiationId, propertyId, clientPartyId, requirementId, intent, load])

  const n = thread?.negotiation
  const closed = !!n?.closed_at
  const events = thread?.events || []
  const priced = events.filter(e => e.amount != null)
  const refresh = () => { setComposing(null); load(n.id) }

  return (
    <div className="fixed inset-0 z-[70] flex items-start sm:items-center justify-center bg-black/50 backdrop-blur-sm p-4"
      {...backdropDismiss}>
      <div className="bg-card border border-border rounded-xl w-full max-w-lg shadow-xl flex flex-col max-h-[90vh]">
        <div className="flex items-center justify-between px-5 py-3.5 border-b border-border shrink-0">
          <span className="font-semibold text-sm"><Icon name="message-circle" size={15} /> {t('nego_title')}</span>
          <button onClick={onClose} className="text-muted-foreground hover:text-foreground text-xl leading-none">&times;</button>
        </div>

        <div className="px-5 py-4 space-y-4 overflow-y-auto flex-1">
          {err && <p className="text-sm text-destructive">{err}</p>}
          {!thread && !err && <p className="text-sm text-muted-foreground">{t('desk_loading')}</p>}

          {thread && (<>
            {/* WHO this is between, and where it stands. */}
            <div className="flex items-start justify-between gap-3">
              <div className="min-w-0 text-sm space-y-0.5">
                <div className="truncate"><Icon name="house" size={13} /> {propertyLabel
                  || n.property?.district_code || t('nego_property_fallback')}</div>
                <div className="truncate"><Icon name="user" size={13} /> {clientLabel
                  || n.client?.name
                  // Another agent's client: the name is withheld everywhere else
                  // too, and the representing agent is who you actually deal with.
                  || (n.client?.rep_name ? tLog('nego_client_of', { name: n.client.rep_name }) : t('nego_client_fallback'))}</div>
              </div>
              <span className={pill('none', { sm: true, extra: STAGE_STYLE[thread.stage] || STAGE_STYLE.open })}>
                {t(`nego_stage_${thread.stage}`)}
              </span>
            </div>

            {/* §11.1 — the two latest positions and the distance between them.
                One number with no counterparty answers nothing. */}
            <div className="rounded-lg border border-border p-3">
              <div className="flex items-start justify-between gap-4">
                <div className="space-y-1">
                  <p className="text-xs tracking-[.02em] text-muted-foreground">{t('nego_latest_position')}</p>
                  {/* Each side carries its OWN currency: nothing stops one side
                      proposing in USD and the other in GEL, and labelling both
                      with a single thread-level currency would misreport one of
                      them. Falls back to the thread currency for older rows. */}
                  <div className="text-sm tabular-nums">
                    <span className="text-muted-foreground w-14 inline-block">{t('nego_side_owner')}</span>
                    <span className="font-medium">{money(thread.owner_position, thread.owner_currency || thread.currency) || '—'}</span>
                  </div>
                  <div className="text-sm tabular-nums">
                    <span className="text-muted-foreground w-14 inline-block">{t('nego_side_client')}</span>
                    <span className="font-medium">{money(thread.client_position, thread.client_currency || thread.currency) || '—'}</span>
                  </div>
                </div>
                {thread.gap != null && (
                  <div className="text-right">
                    <p className="text-xs tracking-[.02em] text-muted-foreground">{t('nego_difference')}</p>
                    <p className="text-lg font-semibold tabular-nums">{money(thread.gap, thread.currency)}</p>
                  </div>
                )}
              </div>

              {/* ⚠️ Say WHY the difference is missing. The server withholds the
                  subtraction across two currencies (it would be a fabricated
                  number); an unexplained blank reads as a bug, and worse, as
                  "they agree". */}
              {thread.mixed_currency && (
                <p className="text-xs text-amber-600 dark:text-amber-400 mt-2">
                  <Icon name="alert-triangle" size={11} /> {t('nego_mixed_currency')}
                </p>
              )}

              {/* CONVERGENCE, NOT A PREDICTION (§11.3). The shape of the gap over
                  time is the honest version of "are they going to agree" — do not
                  turn this into a percentage. */}
              {(thread.gap_history || []).length > 1 && (
                <p className="text-xs text-muted-foreground mt-2">
                  <Icon name="trending-down" size={11} /> {t('nego_converging')}{' '}
                  <span className="tabular-nums">
                    {thread.gap_history.map(g => Number(g.gap).toLocaleString('en-US')).join(' → ')}
                  </span>
                </p>
              )}

              {thread.accepted_amount != null && (
                <p className="text-xs text-emerald-600 dark:text-emerald-400 mt-2">
                  <Icon name="check-circle" size={12} /> {tLog('nego_accepted_at',
                    { amount: money(thread.accepted_amount, thread.currency) })}
                </p>
              )}
            </div>

            {/* What we knew before anyone said anything — the price ladder's rung
                is shown, not just the number, because the ladder degrades and
                "why does it say 550?" must have an answer (§6.4). */}
            <div className="text-xs text-muted-foreground space-y-1">
              {n.seed_amount != null && (
                <p>
                  <Icon name="tag" size={12} /> {tLog('nego_seed', { amount: money(n.seed_amount, n.seed_currency) })}
                  {' '}<span className="opacity-70">({t(`nego_basis_${n.seed_basis || 'none'}`)})</span>
                </p>
              )}
              {thread.floor?.min_price != null && (
                <p>
                  <Icon name="scale" size={12} /> {tLog('nego_floor',
                    { amount: money(thread.floor.min_price, thread.floor.currency) })}
                  {thread.floor.commission_percent != null
                    && ` · ${tLog('nego_commission', { pct: thread.floor.commission_percent })}`}
                </p>
              )}
            </div>

            {/* ADVISORY, NEVER A BLOCK. The very first live share offered 444
                against a 500 floor — which either eats the commission or breaks
                the owner agreement. The system says so; the agent decides. */}
            {thread.below_floor && (
              <p className="rounded-lg border border-amber-500/40 bg-amber-500/10 px-3 py-2 text-xs text-amber-700 dark:text-amber-300">
                <Icon name="alert-triangle" size={12} /> {t('nego_below_floor')}
              </p>
            )}

            {/* The story itself. */}
            <div>
              <p className="text-xs tracking-[.02em] text-muted-foreground mb-2">{t('nego_history')}</p>
              {events.length === 0
                ? <p className="text-xs text-muted-foreground italic">{t('nego_no_events')}</p>
                : <div>{events.map(e => (
                    // "prev" is this SIDE's previous priced event — not simply the
                    // previous event (a note between two counters must not break
                    // the delta) and not the other side's (that is the gap).
                    <EventRow key={e.id} e={e} prev={(() => {
                      const mine = priced.filter(p => p.actor_side === e.actor_side)
                      return mine[mine.indexOf(e) - 1] || null
                    })()} />
                  ))}</div>}
            </div>

            {closed ? (
              <p className="rounded-lg border border-border px-3 py-2 text-xs text-muted-foreground">
                {tLog('nego_closed_banner', { reason: t(`nego_kind_${n.closed_reason}`) })}
              </p>
            ) : composing ? (
              <Compose kind={composing} thread={thread} meetingId={meetingId}
                onCancel={() => setComposing(null)} onDone={refresh} />
            ) : (
              // ⚠️ EVERY ONE OF THESE APPENDS AN EVENT. None of them edits.
              <div className="space-y-2">
                <div className="flex flex-wrap gap-1.5">
                  {['proposal', 'counterproposal', 'acceptance', 'rejection', 'withdrawal', 'note'].map(k => (
                    <button key={k} className={pill('neutral', { door: true, extra: 'text-muted-foreground' })}
                      onClick={() => setComposing(k)}>{t(`nego_kind_${k}`)}</button>
                  ))}
                </div>
                {/* Money is a separate row because it is a separate claim: a deal
                    is a BUSINESS milestone and these are the payment ones, each of
                    which can fail on its own (§6.3). Agent-only, by CHECK. */}
                <div className="flex flex-wrap gap-1.5 pt-1 border-t border-border/60">
                  <span className="text-xs tracking-[.02em] text-muted-foreground self-center mr-1">
                    {t('nego_money_row')}
                  </span>
                  {['deposit', 'contract', 'closing', 'fell_through'].map(k => (
                    <button key={k} className={pill('neutral', { door: true, extra: 'text-muted-foreground' })}
                      onClick={() => setComposing(k)}>{t(`nego_kind_${k}`)}</button>
                  ))}
                </div>
              </div>
            )}
          </>)}
        </div>
      </div>
    </div>
  )
}

// ── The list panel, for the property and client modals ───────────────────────
// §11.4 places negotiations on BOTH modals — the same underlying thread seen from
// two directions, which is free because identity is (property, client).
//
// ⚠️ Read-only, and there is no "new negotiation" button here on purpose: a
// thread needs BOTH sides, and a property modal knows only one of them. Opening
// one starts where both sides are already resolved — a match row, or a meeting.
export function NegotiationsPanel({ propertyId, clientPartyId, propertyLabel, className = '' }) {
  const [rows, setRows] = useState(null)
  const [open, setOpen] = useState(null)

  const load = useCallback(() => {
    if (!propertyId && !clientPartyId) return
    getNegotiations({ propertyId, clientPartyId }).then(setRows).catch(() => setRows([]))
  }, [propertyId, clientPartyId])
  useEffect(() => { load() }, [load])

  // Nothing to say → say nothing. An empty card on every property modal teaches
  // agents to scroll past the section before it ever has content.
  if (!rows?.length) return null

  return (
    <div className={`bg-card border border-border rounded-xl p-4 ${className}`}>
      <p className="text-xs font-semibold text-muted-foreground mb-2">
        <Icon name="message-circle" size={13} /> {t('nego_panel_title')}
      </p>
      <div className="space-y-1.5">
        {rows.map(r => (
          <button key={r.id} type="button" onClick={() => setOpen(r)}
            className="w-full text-left rounded-lg border border-border px-3 py-2 hover:border-primary/40 transition-colors">
            <div className="flex items-center gap-2 flex-wrap">
              <span className="text-sm font-medium truncate">
                {propertyId
                  ? (r.client?.name || (r.client?.rep_name
                      ? tLog('nego_client_of', { name: r.client.rep_name }) : t('nego_client_fallback')))
                  : (r.property?.district_code || t('nego_property_fallback'))}
              </span>
              <span className={pill('none', { sm: true, extra: STAGE_STYLE[r.stage] || STAGE_STYLE.open })}>
                {t(`nego_stage_${r.stage}`)}
              </span>
              <span className="flex-1" />
              <span className="text-xs text-muted-foreground">{timeAgo(r.last_event_at || r.opened_at)}</span>
            </div>
            <div className="text-xs text-muted-foreground tabular-nums mt-0.5">
              {r.owner_position != null || r.client_position != null ? (
                <>
                  {t('nego_side_owner')} {money(r.owner_position, r.owner_currency || r.currency) || '—'}
                  {' · '}
                  {t('nego_side_client')} {money(r.client_position, r.client_currency || r.currency) || '—'}
                  {r.gap != null && <> · {tLog('nego_apart', { amount: money(r.gap, r.currency) })}</>}
                  {r.mixed_currency && <> · {t('nego_mixed_currency_short')}</>}
                </>
              ) : t('nego_no_events')}
            </div>
          </button>
        ))}
      </div>
      {open && (
        <NegotiationModal negotiationId={open.id}
          propertyLabel={propertyId ? propertyLabel : (open.property?.district_code || null)}
          clientLabel={open.client?.name || null}
          onClose={() => { setOpen(null); load() }} />
      )}
    </div>
  )
}
